import { ImageUploader, ImageUploaderType, Input } from "@swivl-mono/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { IoMdInformationCircleOutline } from "react-icons/io";

import { SavedIndicator, useSavedIndicator } from "../../../../../Components/SavedIndicator/SavedIndicator";
import { Tooltip } from "../../../../../Components/Tooltip/Tooltip";
import AccountModel from "../../../../../Models/Account/AccountModel";
import { Member } from "../../../../../Models/Member/Member.Current";
import MembersModel from "../../../../../Models/Member/Members.Model";

interface AccountDetailsFormData {
  firstName: string;
  lastName: string;
}

interface AgentDetailsFormData {
  displayName: string;
}

export function ProfileTab(): React.ReactNode {
  const { account } = AccountModel.useState();
  const member = Member.member;

  const [avatarURL, setAvatarURL] = useState(member?.agentAvatarURL ?? "");
  const accountSaved = useSavedIndicator();
  const agentSaved = useSavedIndicator();

  const accountForm = useForm<AccountDetailsFormData>({
    mode: "onBlur",
    defaultValues: {
      firstName: account?.firstName ?? "",
      lastName: account?.lastName ?? ""
    }
  });

  const agentForm = useForm<AgentDetailsFormData>({
    mode: "onBlur",
    defaultValues: {
      displayName: member?.agentName ?? ""
    }
  });

  if (!account) {
    return null;
  }

  async function handleAccountBlur(): Promise<void> {
    const values = accountForm.getValues();
    if (values.firstName === account?.firstName && values.lastName === account?.lastName) {
      return;
    }
    await AccountModel.updateAccount({ firstName: values.firstName, lastName: values.lastName });
    accountSaved.flash();
  }

  async function handleDisplayNameBlur(): Promise<void> {
    const values = agentForm.getValues();
    if (values.displayName === (member?.agentName ?? "")) {
      return;
    }
    if (member) {
      await MembersModel.updateMember({ id: member.id, agentName: values.displayName });
      agentSaved.flash();
    }
  }

  async function handleAvatarUploaded(url: string): Promise<void> {
    setAvatarURL(url);
    if (member) {
      await MembersModel.updateMember({ id: member.id, agentAvatarURL: url });
      agentSaved.flash();
    }
  }

  async function handleAvatarDeleted(): Promise<void> {
    setAvatarURL("");
    if (member) {
      await MembersModel.updateMember({ id: member.id, agentAvatarURL: "" });
      agentSaved.flash();
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="heading-base-alt text-black">Account Details</span>
          <SavedIndicator visible={accountSaved.show} />
        </div>
        <div className="flex gap-4">
          <Input label="First Name" {...accountForm.register("firstName", { onBlur: handleAccountBlur })} containerClassName="flex-1" />
          <Input label="Last Name" {...accountForm.register("lastName", { onBlur: handleAccountBlur })} containerClassName="flex-1" />
        </div>
        <Input label="Email" value={account.email} disabled containerClassName="w-[276px]" />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <span className="heading-base-alt text-black">Agent Details</span>
          <Tooltip
            content="Your name and avatar will be displayed publicly to all customers across all the locations you have access to"
            placement="bottom"
            classNames={{ tooltip: "text-center body-sm !max-w-64" }}
          >
            <IoMdInformationCircleOutline className="size-4 text-grey-600" />
          </Tooltip>
          <SavedIndicator visible={agentSaved.show} />
        </div>
        <div className="flex items-center gap-4">
          <ImageUploader
            type={ImageUploaderType.AVATAR}
            imageURL={avatarURL}
            imageUploaded={handleAvatarUploaded}
            onImageDeleted={handleAvatarDeleted}
          />
          <Input label="Display Name" {...agentForm.register("displayName", { onBlur: handleDisplayNameBlur })} containerClassName="flex-1" />
        </div>
      </div>
    </div>
  );
}
